import React, { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Grid, IconButton, Modal, Theme, Typography } from "@mui/material";
import { format } from "date-fns";
import { camelCase, endsWith, get } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import { DeleteIconButton, EditIconButton } from "@/components/IconButtons";
import MenBodyComposition from "@/components/Icons/MenBodyComposition";
import { graphql } from "@/gql";
import { BodyCompositionDetails_SettingFragment } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { parseDate } from "@/utils/datetime";
import { apply } from "@/utils/functions";
import { percent } from "@/utils/number";
import { FragmentType } from "@/utils/type";

import ViewBodyCompositionModalItem from "./ViewBodyCompositionModalItem";

interface ViewBodyCompositionModalType {
  settings?: BodyCompositionDetails_SettingFragment;
  bcByDay?: FragmentType<typeof viewBodyCompositionModalFragment>;
  visibility: boolean;
  onEdit: (date: Date) => void;
  onDelete: () => void;
  onClose: () => void;
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
  },
  modal: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    height: "80%",
    overflow: "hidden",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    width: "60%",
    marginTop: theme.spacing(4),
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2, 4),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
    padding: theme.spacing(2, 4),
  },
  tabs: {
    display: "flex",
    borderTop: "1px solid #DEE6F0",
    borderBottom: "1px solid #DEE6F0",
    width: "100%",
    padding: "10px",
  },
  link: {
    textDecoration: "none",
    color: "inherit",
    width: "auto",
    height: 48,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4, 4),
  },
  activeLink: {
    color: "#26C9A7",
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  headerText: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    margin: theme.spacing(4, 3),
  },
  container: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    overflowY: "auto",
    height: "calc(100% - 70px)",
  },
  iconContainer: {
    width: "100%",
    height: "100%",
    padding: "40px",
  },
  icon: {
    width: "100%",
    height: "100%",
  },
  time: {
    fontSize: "16px",
    color: colors.gray,
    margin: "0px 10px",
  },
}));

const FullGrid = withStyles(Grid, {
  root: {
    height: "calc(100% - 8px)",
  },
});

const StyledGrid = withStyles(Grid, {
  root: {
    height: "100%",
  },
});

const viewBodyCompositionModalFragment = graphql(`
  fragment ViewBodyCompositionModal on BodyCompositionsByDay {
    day
    bodyComposition {
      ...BodyCompositionDetails
    }
  }
`);

const ViewBodyCompositionModal: React.FC<ViewBodyCompositionModalType> = ({
  visibility,
  onEdit,
  onClose,
  settings,
  bcByDay,
  onDelete,
}) => {
  const { classes } = useStyles();
  const dateCreated = bcByDay?.day
    ? format(new Date(bcByDay?.day), "dd MMM, yyyy")
    : "N/A";
  const timeCreated = bcByDay?.bodyComposition.createdAt
    ? format(new Date(bcByDay.bodyComposition.createdAt), "hh:mm bb")
    : "-";

  const getFieldValue = (
    fieldName: string,
    value: number | null
  ): string | number | null => {
    if (value === null) return null;
    if (endsWith(fieldName, "Percentage")) {
      return `${percent(value, { round: 2 })} %`;
    }
    return value;
  };

  const displayFields = useMemo(
    () => () => {
      if (settings) {
        return settings.bodyCompositionFields?.map((field, index) => {
          const key = camelCase(field.fieldName);
          const value = get(bcByDay?.bodyComposition, key);
          if (value !== null) {
            return (
              <ViewBodyCompositionModalItem
                key={index}
                title={field.fieldLabel}
                value={getFieldValue(key, value)}
              />
            );
          } else if (field.visible) {
            const fieldValue = getFieldValue(key, value);
            return (
              <ViewBodyCompositionModalItem
                key={index}
                title={field.fieldLabel}
                value={fieldValue != null ? value : "-"}
              />
            );
          }
        });
      }
    },
    [settings, bcByDay?.bodyComposition]
  );

  return (
    <Modal
      open={visibility}
      onClose={() => {
        onClose();
      }}
      className={classes.root}
    >
      <Box className={classes.modal}>
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              {dateCreated}
              <span className={classes.time}>&#x2022; {timeCreated}</span>
            </Typography>

            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center">
            <div className={classes.tabs}>
              <Typography variant="subtitle2">Body Composition</Typography>
            </div>
          </Box>
          <Box height="calc(100% - 150px)">
            <FullGrid container>
              <StyledGrid item xs={4}>
                <Box className={classes.iconContainer}>
                  <MenBodyComposition className={classes.icon} />
                </Box>
              </StyledGrid>
              <StyledGrid item xs={8}>
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Typography className={classes.headerText}>
                    Body Composition
                  </Typography>

                  <Box display="flex" alignItems="center">
                    <EditIconButton
                      onClick={() =>
                        onEdit(apply(bcByDay?.day, parseDate) ?? new Date())
                      }
                    />
                    <Box marginX={1} />
                    <DeleteIconButton onClick={onDelete} />
                    <Box marginX={1} />
                  </Box>
                </Box>
                <Box className={classes.container}>{displayFields()}</Box>
              </StyledGrid>
            </FullGrid>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ViewBodyCompositionModal;
